import { useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Letterhead, type LetterheadCompany } from '@/components/company/Letterhead';
import { ErrorState, LoadingState } from '@/components/ui/states';
import { DocumentTerms } from '@/components/company/DocumentTerms';
import { formatCurrency, formatDate, formatDateOnly } from '@/lib/format';
import { quantityText, unitPriceText } from '@/lib/lineDisplay';
import {
  PublicInvoiceGoneError,
  usePublicInvoice,
  type PublicInvoice as PublicInvoiceData,
  type PublicInvoiceDetail,
} from '@/routes/publicInvoice/usePublicInvoice';

/** The states that carry the bill. Anything else — a state a later API adds — is not guessed at. */
const READABLE = new Set(['Open', 'Partial', 'Overdue', 'Paid']);

/**
 * The customer's invoice, opened from the link in the invoice email or a
 * reminder, with no sign-in (M2-16, pass B) — M2-07's estimate page, for
 * invoices.
 *
 * ⚠️ Routed OUTSIDE `RequireAuth` and `AppLayout` (see `App.tsx`), and read
 * through `publicClient`, not `apiClient` — no bearer token, and no 401 ever
 * sends the customer to the office's sign-in page.
 *
 * <b>It shows exactly what the API returns, and nothing the API leaves
 * out.</b> Every figure is the API's — nothing here adds a line or a total.
 * A void invoice's response has no bill (`invoice` is null), so its page
 * has no amounts: just that it was cancelled, by whom and when. Every dead
 * link is one "no longer valid" page that names no shop.
 *
 * <b>Clocks.</b> The due date is a `DateOnly` on the shop's calendar
 * (`formatDateOnly`, UTC, so it never reads a day early west of UTC). The
 * sent and voided instants are shown as dates on the SHOP's clock — the
 * response's `timezone` — so "Sent" can never read a day after the "Due"
 * beside it for a customer in another zone. A zone this browser cannot read
 * leaves the sent date out rather than guess.
 *
 * No Pay button yet: M2-13 adds the payment link (the API has no field for it).
 */
export function PublicInvoice() {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const invoice = usePublicInvoice(token);

  if (invoice.isPending) {
    return (
      <Page>
        <LoadingState label={t('publicInvoice.loading')} className="justify-center" />
      </Page>
    );
  }

  if (invoice.error instanceof PublicInvoiceGoneError) {
    return <Gone />;
  }

  if (invoice.isError) {
    return (
      <Page>
        <ErrorState
          title={t('publicInvoice.loadErrorTitle')}
          description={t('publicInvoice.loadErrorDescription')}
          onRetry={() => void invoice.refetch()}
        />
      </Page>
    );
  }

  return <Loaded invoice={invoice.data} />;
}

function Gone() {
  const { t } = useTranslation();
  return (
    <Page>
      <Explained title={t('publicInvoice.goneTitle')}>{t('publicInvoice.goneDescription')}</Explained>
    </Page>
  );
}

function Loaded({ invoice }: { invoice: PublicInvoiceData }) {
  const { t } = useTranslation();
  const company = invoice.companyName;
  const letterhead: LetterheadCompany = {
    name: company,
    address: invoice.company?.address ?? null,
    phone: invoice.company?.phone ?? null,
    licenseNumber: invoice.company?.licenseNumber ?? null,
    logoPath: invoice.company?.logoPath ?? null,
  };
  const number = Number(invoice.number);
  const title = t('publicInvoice.title', { number });
  const zone = readableZone(invoice.timezone);

  useDocumentTitle(t('publicInvoice.documentTitle', { title, company }));

  if (invoice.state === 'Void') {
    const voidedOn = invoice.voidedAt && zone ? shopDate(invoice.voidedAt, zone) : null;
    return (
      <Page company={letterhead}>
        <Explained title={t('publicInvoice.voidTitle')} eyebrow={title}>
          {voidedOn
            ? t('publicInvoice.voidDescription', { company, number, date: voidedOn })
            : t('publicInvoice.voidDescriptionNoDate', { company, number })}
        </Explained>
      </Page>
    );
  }

  if (!invoice.invoice || !READABLE.has(invoice.state)) {
    return <Gone />;
  }

  return (
    <Page company={letterhead}>
      <Bill title={title} state={invoice.state} zone={zone} detail={invoice.invoice} />
    </Page>
  );
}

function Bill({
  title,
  state,
  zone,
  detail,
}: {
  title: string;
  state: string;
  zone: string | null;
  detail: PublicInvoiceDetail;
}) {
  const { t } = useTranslation();
  const address = detail.serviceAddress;
  const due = formatDateOnly(detail.dueDate);

  return (
    <article aria-labelledby="public-invoice-title" className="space-y-6">
      <div className="space-y-1">
        <h1 id="public-invoice-title" className="text-2xl font-semibold tracking-tight">
          {title}
        </h1>
        {zone ? (
          <p className="text-sm text-[var(--color-muted-foreground)]" data-testid="public-sent">
            {t('publicInvoice.sentOn', { date: shopDate(detail.sentAt, zone) })}
          </p>
        ) : null}
      </div>

      <p
        className={
          state === 'Overdue'
            ? 'rounded-md border-l-4 border-[var(--color-destructive)] bg-[var(--color-muted)] px-4 py-3 font-medium'
            : 'rounded-md border-l-4 border-[var(--color-primary)] bg-[var(--color-muted)] px-4 py-3 font-medium'
        }
        data-testid="public-state"
      >
        {t(`publicInvoice.state.${state}`, { date: due })}
      </p>

      <section aria-labelledby="public-invoice-for" className="space-y-1">
        <h2
          id="public-invoice-for"
          className="text-xs font-medium tracking-wide text-[var(--color-muted-foreground)] uppercase"
        >
          {t('publicInvoice.billedTo')}
        </h2>
        <p className="font-medium" data-testid="billed-to">
          {detail.customerName}
        </p>
        {address ? (
          <address className="text-sm not-italic" data-testid="service-address">
            <span className="sr-only">{t('publicInvoice.serviceAt')}: </span>
            {address.line1}
            <br />
            {address.line2 ? (
              <>
                {address.line2}
                <br />
              </>
            ) : null}
            {t('publicEstimate.cityLine', {
              city: address.city,
              state: address.state,
              postalCode: address.postalCode,
            })}
          </address>
        ) : null}
      </section>

      <section aria-labelledby="public-invoice-lines" className="space-y-2">
        <h2 id="public-invoice-lines" className="text-base font-semibold">
          {t('publicInvoice.linesHeading')}
        </h2>
        <ul className="divide-y divide-[var(--color-border)] border-y border-[var(--color-border)]">
          {detail.lines.map((line, index) => (
            <li
              // Lines carry no id on the public response, by design; they are
              // never reordered here, so the position is a stable key.
              key={index}
              className="flex items-start justify-between gap-4 py-3"
              data-testid="public-line"
            >
              <div className="min-w-0 space-y-0.5">
                <p className="break-words">{line.description}</p>
                <p className="text-sm text-[var(--color-muted-foreground)]" data-testid="public-line-qty">
                  {line.hourly
                    ? t('publicInvoice.hoursAtRate', {
                        hours: quantityText(line, t),
                        rate: unitPriceText(line, t),
                      })
                    : t('publicInvoice.qtyAtPrice', {
                        quantity: Number(line.quantity),
                        price: formatCurrency(Number(line.unitPrice)),
                      })}
                </p>
                {Number(line.discount) > 0 ? (
                  <p className="text-sm text-[var(--color-muted-foreground)]">
                    {t('publicInvoice.discount', { amount: formatCurrency(Number(line.discount)) })}
                  </p>
                ) : null}
              </div>
              <p className="shrink-0 text-right font-medium tabular-nums">
                {formatCurrency(Number(line.total))}
              </p>
            </li>
          ))}
        </ul>
        <dl
          className="ml-auto grid max-w-xs grid-cols-[1fr_auto] gap-x-6 gap-y-1 pt-2 text-sm"
          data-testid="public-totals"
        >
          <dt>{t('publicInvoice.subtotal')}</dt>
          <dd className="text-right tabular-nums" data-testid="public-subtotal">
            {formatCurrency(Number(detail.subtotal))}
          </dd>
          <dt>{t('publicInvoice.tax')}</dt>
          <dd className="text-right tabular-nums" data-testid="public-tax">
            {formatCurrency(Number(detail.tax))}
          </dd>
          <dt className="font-semibold">{t('publicInvoice.total')}</dt>
          <dd className="text-right font-semibold tabular-nums" data-testid="public-total">
            {formatCurrency(Number(detail.total))}
          </dd>
          {/* Kyle 2a: shown once something has been paid — "$0.00 paid" on a
              bill nobody has paid yet is noise. */}
          {Number(detail.amountPaid) > 0 ? (
            <>
              <dt>{t('publicInvoice.amountPaid')}</dt>
              <dd className="text-right tabular-nums" data-testid="public-paid">
                {formatCurrency(Number(detail.amountPaid))}
              </dd>
            </>
          ) : null}
          <dt className="text-base font-semibold">{t('publicInvoice.balanceDue')}</dt>
          <dd className="text-right text-base font-semibold tabular-nums" data-testid="public-balance">
            {formatCurrency(Number(detail.balanceDue))}
          </dd>
        </dl>
      </section>

      {/* M2-48: the terms and warranty this invoice went out with. */}
      <DocumentTerms
        terms={detail.terms?.terms}
        warrantyStatement={detail.terms?.warrantyStatement}
        termsHeading={t('documentTerms.invoiceTerms')}
      />
    </article>
  );
}

/** The instant's calendar date on the shop's clock. */
function shopDate(instant: string, zone: string): string {
  return formatDate(instant, { dateStyle: 'medium', timeZone: zone });
}

/** The zone, if this browser can format on it — `Intl` throws on one it does not know. */
function readableZone(zone: string | null | undefined): string | null {
  if (!zone) return null;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: zone });
    return zone;
  } catch {
    return null;
  }
}

/** The page's frame: the shop's letterhead as the header, then one readable column. */
function Page({ company, children }: { company?: LetterheadCompany; children: ReactNode }) {
  return (
    <div className="min-h-screen bg-[var(--color-background)]">
      {company ? (
        <header className="border-b border-[var(--color-border)]">
          <div className="mx-auto max-w-2xl px-4 py-4">
            <Letterhead company={company} />
          </div>
        </header>
      ) : null}
      <main className="mx-auto max-w-2xl space-y-6 px-4 py-6">{children}</main>
    </div>
  );
}

/** A link with no bill to show: one heading and the sentence that explains it. */
function Explained({
  title,
  eyebrow,
  children,
}: {
  title: string;
  eyebrow?: string;
  children: ReactNode;
}) {
  return (
    <section aria-labelledby="public-invoice-explained" className="space-y-3 pt-6 text-center">
      {eyebrow ? <p className="text-sm text-[var(--color-muted-foreground)]">{eyebrow}</p> : null}
      <h1 id="public-invoice-explained" className="text-xl font-semibold tracking-tight">
        {title}
      </h1>
      <p className="mx-auto max-w-prose text-sm text-[var(--color-muted-foreground)]" data-testid="explained">
        {children}
      </p>
    </section>
  );
}

/** The tab's title while the page is open, so the customer can find it among others. */
function useDocumentTitle(title: string) {
  useEffect(() => {
    const previous = document.title;
    document.title = title;
    return () => {
      document.title = previous;
    };
  }, [title]);
}
